import apiClient from "./apiClient";
const API_URL = `/emergency-admissions`; // Use relative path since apiClient has baseURL

export const createAdmission = async (emergencyData) => {
  // Remove organizationId from data if it exists (apiClient will add it as query param for POST requests)
  // This ensures the backend routes to the correct organization-specific table
  const { organizationId, ...admissionData } = emergencyData;
  
  // apiClient will automatically add organizationId as query parameter via interceptor
  // This ensures records are created in organization-specific tables (e.g., 23_emergency_admission)
  // instead of the base emergency_admission table
  const response = await apiClient.post(`${API_URL}/create`, admissionData);
  return response.data;
};

export const updateAdmission = async (id, data) => {
  // Remove organizationId from data if it exists (apiClient will add it as query param for PUT requests)
  // This ensures the backend routes to the correct organization-specific table
  const { organizationId, ...admissionData } = data;
  
  // apiClient will automatically add organizationId as query parameter via interceptor
  // This ensures records are updated in organization-specific tables (e.g., 23_emergency_admission)
  // instead of the base emergency_admission table
  const response = await apiClient.put(`${API_URL}/${id}`, admissionData);
  return response.data;
};

export const getAllEmergencyAdmissions = async () => {
  try {
    // apiClient will automatically add organizationId as query param via interceptor
    const response = await apiClient.get(API_URL);
    let data = response.data;
    
    // Ensure data is an array
    if (!Array.isArray(data)) {
      return [];
    }
    
    // Filter out invalid/placeholder data
    // Only include entries that have valid required fields
    const filteredData = data.filter(item => {
      // Must have a valid id
      if (!item || item.id == null || item.id === undefined) {
        return false;
      }
      // Must have at least one meaningful field (not all null/empty)
      const hasValidData = item.patientName || item.emergencyId || item.attendantName || item.attendantContact;
      return hasValidData;
    });
    
    return filteredData;
  } catch (error) {
    // Handle case where organization-specific table doesn't exist yet
    if (error.response?.status === 500 && 
        (error.response?.data?.message?.includes("Failed to retrieve") ||
         error.response?.data?.message?.includes("from table") ||
         error.response?.data?.message?.includes("does not exist"))) {
      console.warn("Organization-specific table not found, returning empty array:", error.response.data.message);
      return []; // Return empty array if table doesn't exist
    }
    console.error("Error fetching emergency admissions:", error);
    // Return empty array on any error to prevent showing default data
    return [];
  }
};

export const getAdmissionById = async (id) => {
  // Get organizationId from localStorage to validate response
  let expectedOrganizationId = null;
  const storedUser = localStorage.getItem("user");
  if (storedUser) {
    try {
      const user = JSON.parse(storedUser);
      const orgId = user?.organizationId;
      if (orgId != null && orgId !== "" && !isNaN(orgId) && Number(orgId) > 0) {
        expectedOrganizationId = Number(orgId);
      }
    } catch (e) {
      console.error("Error parsing stored user:", e);
    }
  }
  
  // apiClient will automatically add organizationId as query param via interceptor
  // This ensures data is fetched from organization-specific table (e.g., 23_emergency_admission)
  // instead of the base emergency_admission table
  const response = await apiClient.get(`${API_URL}/${id}`);
  const data = response.data;
  
  // Validate that the returned record belongs to the expected organization
  // This prevents showing data from wrong organization or base table
  if (expectedOrganizationId && data && data.organizationId != null) {
    const returnedOrgId = Number(data.organizationId);
    if (returnedOrgId !== expectedOrganizationId) {
      console.error(
        `[EmergencyAdmissionService] Organization mismatch: Expected ${expectedOrganizationId}, got ${returnedOrgId} for admission ID ${id}. ` +
        `This may indicate the backend is returning data from the wrong table.`
      );
      // Throw an error to prevent showing incorrect data
      throw new Error(
        `Emergency admission record belongs to a different organization. ` +
        `Expected organization ${expectedOrganizationId}, but record belongs to organization ${returnedOrgId}.`
      );
    }
  }
  
  return data;
};

export default {
  createAdmission,
  getAllEmergencyAdmissions,
  getAdmissionById
}